import { Children, Fragment, forwardRef, isValidElement, type CSSProperties, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { cx } from "./fieldContext";
const SPACE_SIZE_PRESETS: readonly string[] = ["small", "middle", "large"];

export type SpaceSize = "small" | "middle" | "large" | number;

export interface SpaceProps extends HTMLAttributes<HTMLDivElement> {
  /** Abstand wie bei Antd: Stufe oder Pixel, als Paar `[waagerecht, senkrecht]`. Default: "small". */
  size?: SpaceSize | [SpaceSize, SpaceSize];
  orientation?: "horizontal" | "vertical";
  /** @deprecated wie bei Antd: `orientation`. */
  direction?: "horizontal" | "vertical";
  vertical?: boolean;
  align?: "start" | "end" | "center" | "baseline";
  wrap?: boolean;
  split?: ReactNode;
  separator?: ReactNode;
  style?: CSSProperties;
}

/**
 * Kinder flach machen wie Antd (`toArray`): Fragmente auflösen, leere Kinder überspringen. Die Hüllen
 * brauchen eindeutige Schlüssel: `Children.toArray` trennt eigene Schlüssel („.$6") von Positionen („.6"),
 * Fragmente stellen ihren Schlüssel voran. Roh übernommene `child.key` neben Indexschlüsseln kollidierten
 * (Listenzeile „6" neben dem siebten Kind), React verwaiste dann Zeilen und Eingaben trafen die falsche.
 */
function flatten(children: ReactNode, out: Array<{ key: string; node: ReactNode }> = [], prefix = ""): Array<{ key: string; node: ReactNode }> {
  Children.toArray(children).forEach((child, index) => {
    if (isValidElement(child) && child.type === Fragment) {
      flatten((child as ReactElement<{ children?: ReactNode }>).props.children, out, `${prefix}${String(child.key)}/`);
      return;
    }
    out.push({ key: `${prefix}${isValidElement(child) && child.key != null ? String(child.key) : `#${index}`}`, node: child });
  });
  return out;
}

const gapValue = (size: SpaceSize) => (typeof size === "number" ? `${size}px` : `var(--mq-kit-gap-${size})`);

/**
 * Abstandscontainer des Kits (Ersatz für Antd `Space`). Verhält sich im Layout wie das Original —
 * `inline-flex`, je Kind eine Hülle, waagerecht mittig ausgerichtet —, damit bestehende Seiten
 * ohne Umbau gleich bleiben.
 */
export const SpaceBase = forwardRef<HTMLDivElement, SpaceProps>(function Space(
  { size = "small", orientation, direction, vertical, align, wrap, split, separator, className, style, children, ...rest },
  ref,
) {
  const isVertical = vertical ?? (orientation ?? direction) === "vertical";
  const [columnSize, rowSize] = Array.isArray(size) ? size : [size, size];
  const mergedAlign = align ?? (isVertical ? undefined : "center");
  const divider = separator ?? split;
  const items = flatten(children);
  return (
    <div
      ref={ref}
      className={cx("ck-space", isVertical ? "ck-space--vertical" : "ck-space--horizontal", wrap && "ck-space--wrap", mergedAlign && `ck-space--align-${mergedAlign}`, className)}
      style={{ columnGap: gapValue(columnSize), rowGap: gapValue(rowSize), ...style }}
      {...rest}
    >
      {items.map(({ key, node }, index) => (
        <Fragment key={key}>
          {divider != null && index > 0 ? <span className="ck-space-split">{divider}</span> : null}
          <div className="ck-space-item">{node}</div>
        </Fragment>
      ))}
    </div>
  );
});

export interface FlexProps extends HTMLAttributes<HTMLElement> {
  vertical?: boolean;
  orientation?: "horizontal" | "vertical";
  wrap?: boolean | CSSProperties["flexWrap"];
  justify?: CSSProperties["justifyContent"];
  align?: CSSProperties["alignItems"];
  flex?: CSSProperties["flex"];
  /** Abstand wie bei Antd: Stufe oder CSS-Wert. */
  gap?: "small" | "middle" | "large" | number | string;
  component?: keyof HTMLElementTagNameMap;
  style?: CSSProperties;
}

/** Flex-Container des Kits (Ersatz für Antd `Flex`, gleiche Props). */
export const Flex = forwardRef<HTMLElement, FlexProps>(function Flex(
  { vertical, orientation, wrap, justify, align, flex, gap, component = "div", className, style, children, ...rest },
  ref,
) {
  const Component = component as "div";
  const isVertical = vertical ?? orientation === "vertical";
  const gapCss = gap == null ? undefined : typeof gap === "string" && SPACE_SIZE_PRESETS.includes(gap) ? `var(--mq-kit-gap-${gap})` : typeof gap === "number" ? `${gap}px` : gap;
  return (
    <Component
      ref={ref as never}
      className={cx("ck-flex", className)}
      style={{
        flexDirection: isVertical ? "column" : undefined,
        flexWrap: wrap === true ? "wrap" : wrap === false ? undefined : wrap,
        justifyContent: justify,
        alignItems: align,
        flex,
        gap: gapCss,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
});
